/**
 * One person in a video meeting: their camera when it is on, their initials
 * when it is off, their name, and whether they are muted or talking. Our own
 * picture is mirrored, the way every camera app does it, so raising your
 * right hand raises it on the right.
 *
 * Three sizes: `fill` takes whatever box it is given (the stage, or the
 * picture-in-picture corner), the default is a 16:9 grid cell, and `compact`
 * is the strip under a shared screen.
 */
import { MicOff } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { Track } from 'livekit-client';
import { useI18n } from '../../contexts/I18nContext';
import type { MeetMember } from '../../hooks/useMeetRoom';
import { Avatar } from '../tasks/linear/Avatar';

function CameraVideo({ track, mirrored }: { track: Track; mirrored: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  // Attached imperatively: the MediaStream is the SDK's, and detaching on
  // cleanup is what stops the decode once the tile is gone.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      // Audio plays through the room's audio elements; never from a tile.
      muted
      className={`h-full w-full object-cover ${mirrored ? '-scale-x-100' : ''}`}
    />
  );
}

export function VideoTile({
  member,
  speaking,
  size = 'cell',
  className = '',
}: {
  member: MeetMember;
  speaking: boolean;
  size?: 'fill' | 'cell' | 'compact';
  className?: string;
}) {
  const { t } = useI18n();
  const avatarSize = size === 'compact' ? 40 : size === 'fill' ? 96 : 72;
  return (
    <div
      className={[
        // `ring-inset`: a plain ring is painted OUTSIDE the box, so at the edge
        // of the grid (or of the stage) the speaking border was sliced off by
        // the container. Inset, it is drawn inside the tile, follows the same
        // rounded corners, and closes all the way round whatever the layout.
        'relative overflow-hidden rounded-xl bg-card ring-2 ring-inset transition-[box-shadow,ring-color]',
        speaking ? 'ring-emerald-400' : 'ring-transparent',
        size === 'fill'
          ? 'h-full w-full'
          : size === 'compact'
            ? 'h-full w-36 shrink-0'
            : 'aspect-video w-full',
        className,
      ].join(' ')}
    >
      {member.camOn && member.cameraTrack ? (
        <CameraVideo track={member.cameraTrack} mirrored={member.isLocal} />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted">
          <Avatar name={member.name} size={avatarSize} />
        </div>
      )}
      {/* The name as a solid chip rather than text on a gradient: a gradient
          still loses against a bright window behind the speaker, and the old
          full-width bar let the name run to the very edge of the tile. Black
          plate, white bold text — legible over any picture. `text-fixed-white`,
          not `text-white`: the palette white flips to a dark surface in the
          dark theme, which painted the names near-black on the black plate. */}
      <div
        className={`absolute bottom-2 left-2 flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-md bg-black/75 backdrop-blur-sm ${
          size === 'compact' ? 'px-1.5 py-0.5' : 'px-2 py-1'
        }`}
      >
        {!member.micOn && (
          <span title={t('meet.controls.muted')} className="shrink-0 text-red-400">
            <MicOff size={size === 'compact' ? 12 : 14} />
          </span>
        )}
        <span
          className={`truncate font-bold text-fixed-white ${size === 'compact' ? 'text-[11px]' : 'text-sm'}`}
        >
          {member.isLocal ? t('meet.you') : member.name}
        </span>
      </div>
    </div>
  );
}
